import type { ReactElement } from "react";
import { ArrowUpRight } from "lucide-react";

// Real proof, not fictional mockups (research: "proof that answers the
// specific fear" beats generic premium-service language — no invented
// testimonials or staged screenshots). Approved by Max, 2026-09-09. Images
// are real screenshots in public/real-sites/, captured from the live sites.
const REAL_SITES = [
  {
    domain: "starbuckjazz.com",
    name: "Starbuck Jazz Quartet",
    kicker: "Music",
    detail: "Shows, a gallery, and a way to get in touch.",
    image: "/real-sites/starbuckjazz.jpg",
  },
  {
    domain: "autowa.sh",
    name: "Autowash",
    kicker: "Home services",
    detail: "Book a home car wash and see the receipt up front.",
    image: "/real-sites/autowash.jpg",
  },
  {
    domain: "usesusan.com",
    name: "Susan",
    kicker: "Business services",
    detail: "LLC formation and compliance, kept in one place.",
    image: "/real-sites/usesusan.jpg",
  },
] as const;

export function ExampleSites(): ReactElement {
  return (
    <section id="examples" className="px-5 pb-20 sm:px-8 sm:pb-28">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-2xl text-center">
          <div className="marketing-kicker text-[#45635e]">Real, live</div>
          <h2 className="marketing-display mt-5 text-4xl leading-[1.02] text-[#12211d] sm:text-5xl">
            Not mockups. Sites Chill runs today.
          </h2>
          <p className="mt-5 text-lg leading-8 text-[#5e6d69]">
            Each one is a real business, kept current by text. Visit any of
            them, or describe your own below.
          </p>
        </div>
        <ul className="mt-12 grid gap-6 md:grid-cols-3">
          {REAL_SITES.map((site) => (
            <li key={site.domain}>
              <a
                href={`https://${site.domain}`}
                target="_blank"
                rel="noreferrer noopener"
                className="group block rounded-[1.5rem] border border-[#d5e9f7] bg-white p-2 transition-colors duration-200 hover:border-[#b8b9dd]"
              >
                <SiteThumb site={site} />
                <div className="flex items-center justify-between px-3 py-3">
                  <span className="text-sm font-medium text-[#1b2b27]">
                    {site.name}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-[#65736f] transition-colors group-hover:text-[#20272b]">
                    {site.domain}
                    <ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5" />
                  </span>
                </div>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function SiteThumb({
  site,
}: {
  site: (typeof REAL_SITES)[number];
}): ReactElement {
  return (
    <div className="overflow-hidden rounded-[1.15rem] border border-[#dfe8e5] bg-[#f3f6f2]">
      <header className="flex items-center justify-between border-b border-[#dfe8e5] px-4 py-3">
        <div className="text-[10px] font-semibold tracking-[0.2em] text-[#1e342f]">
          {site.name.toUpperCase()}
        </div>
        <span className="text-[10px] uppercase tracking-[0.14em] text-[#8a9692]">
          {site.kicker}
        </span>
      </header>
      <img
        src={site.image}
        alt={`${site.name}'s live site`}
        loading="lazy"
        className="block h-[11.5rem] w-full object-cover object-top"
      />
      <div className="border-t border-white/70 bg-white/62 px-4 py-3 text-xs text-[#65736f]">
        {site.detail}
      </div>
    </div>
  );
}
